import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './attendance.css';

// Team → Attendance & Leaves (also a tab on the Salaries page). Managers /
// supervisors / admins fill this; it carries no pay figures.
//
// Presence is DERIVED, never set by hand for cleaners:
//   • Cleaners  → present on scheduled days (from bookings). LOCKED — fix the
//                 booking, not the grid. Their first day off in a FINISHED week
//                 is auto Weekly Off; a second day off turns red (anomaly).
//   • Everyone else → no schedule, so nothing is auto-filled; a manager marks
//                 Present / Weekly Off / leave on each day.
// Whole-day company Holidays are toggled in the Holiday row. AL / UL / H / OT
// are summed at the right. Click a cell to cycle; click-drag to paint several
// days at once. Nothing saves until the floating Save button is pressed.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const META = {
    present: { code: 'P', cls: 'att--p', label: 'Present' },
    weekly_off: { code: 'WO', cls: 'att--wo', label: 'Weekly Off' },
    annual_leave: { code: 'AL', cls: 'att--al', label: 'Annual Leave' },
    paid_leave: { code: 'PL', cls: 'att--pl', label: 'Paid Leave' },
    unpaid_leave: { code: 'UL', cls: 'att--ul', label: 'Unpaid Leave' },
    lieu_off: { code: 'UH', cls: 'att--lieu', label: 'Unused Holiday Leave' },
};
// A single-cell click cycles through these; a multi-day drag opens the picker.
// lieu_off (UH) is in the cycle but SKIPPED unless the person has a live UH
// balance (same gate as the picker), so it only ever appears when takeable.
const CYCLE_CLEANER = [null, 'weekly_off', 'annual_leave', 'paid_leave', 'unpaid_leave', 'lieu_off'];
const CYCLE_STAFF = [null, 'present', 'weekly_off', 'annual_leave', 'paid_leave', 'unpaid_leave', 'lieu_off'];
// Status options in the picker. Cleaners can't be set Present (it's from the
// schedule); everyone else can. lieu_off appears only when the staff member has
// a positive holiday-in-lieu balance (filtered where the menu is built).
const MENU_OPTIONS = ['present', 'weekly_off', 'annual_leave', 'paid_leave', 'unpaid_leave', 'lieu_off'];
// Statuses that mean "not working that day" (mirrors the backend LEAVE_OFF).
const LEAVE_OFF_STATUSES = ['weekly_off', 'annual_leave', 'paid_leave', 'unpaid_leave', 'lieu_off'];

// Live annual-leave figures for one person, folding in unsaved (pending) edits
// to THIS month. Two different numbers, and they are not interchangeable:
//   left  — entitlement minus leave taken up to the end of this month. What the
//           "Left" column shows, and what pairs with the accrual shown next to it.
//   ahead — leave already booked in LATER months. Not spent yet, so it isn't
//           subtracted from `left`; it's shown beside it so nobody books a month
//           twice without noticing.
//   bookable — left minus ahead. The only figure the no-negative guard may spend,
//           so pre-booking August can't let someone overdraw in July.
// Returns null for staff with no entitlement (not on a company ID).
function annualFor(s, pendingObj, dim) {
    if (!s || !s.annual_left) return null;
    let savedAL = 0, effAL = 0;
    for (let d = 1; d <= dim; d++) {
        const saved = s.marks[d] || null;
        const key = `${s.user_id}-${d}`;
        const eff = key in pendingObj ? (pendingObj[key] === 'clear' ? null : pendingObj[key]) : saved;
        if (saved === 'annual_leave') savedAL += 1;
        if (eff === 'annual_leave') effAL += 1;
    }
    const delta = effAL - savedAL;
    return {
        left: s.annual_left.balance - delta,
        ahead: s.annual_left.booked_ahead || 0,
        bookable: s.annual_left.remaining - delta,
    };
}

// How many annual-leave days this person can still be MARKED, given the saved
// balance, leave already booked in later months, and any unsaved changes this
// month. 0 for non-eligible.
function annualRemainingFor(s, pendingObj, dim) {
    const a = annualFor(s, pendingObj, dim);
    return a ? a.bookable : 0;
}

// Saved UH balance minus any unsaved lieu_off marks added this month. A person
// with no saved credits still gets pending takes counted (base 0), so the LIVE
// figure (liveBH = this + pending banked holidays) stays exact either way.
function lieuRemainingFor(s, pendingObj, dim) {
    if (!s) return 0;
    let savedLieu = 0, effLieu = 0;
    for (let d = 1; d <= dim; d++) {
        const saved = s.marks[d] || null;
        const key = `${s.user_id}-${d}`;
        const eff = key in pendingObj ? (pendingObj[key] === 'clear' ? null : pendingObj[key]) : saved;
        if (saved === 'lieu_off') savedLieu += 1;
        if (eff === 'lieu_off') effLieu += 1;
    }
    return (s.lieu ? s.lieu.balance : 0) - (effLieu - savedLieu);
}

export default function Attendance({ standalone = false }) {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pending, setPending] = useState({});      // "userId-day" -> status | 'clear'
    const [pendingHol, setPendingHol] = useState({}); // day -> desired boolean
    const [pendingHolTreat, setPendingHolTreat] = useState({}); // day -> 'pay'|'bank' (holiday default)
    const [pendingOverride, setPendingOverride] = useState({}); // "day|userId" -> 'pay'|'bank'|'clear'
    const [pendingNotes, setPendingNotes] = useState({});       // userId -> note text
    const [holMenu, setHolMenu] = useState(null);     // { day, x, y } holiday pay/bank popover
    const [saving, setSaving] = useState(false);
    const [selection, setSelection] = useState(null); // { userId, days:Set } while selecting
    const [menu, setMenu] = useState(null);           // { userId, days:[], x, y } status picker
    const [lock, setLock] = useState(null);           // { user_id, name, mine, ttl_seconds } | null
    const [editMode, setEditMode] = useState(false);  // floating button toggles this (Start Editing → Save)
    const dragging = useRef(false);
    const selectionRef = useRef(null);
    const dataRef = useRef(data);
    const pendingRef = useRef(pending);
    // Fresh live-UH-balance function for the window-level mouseup handler (its
    // closure is stale; refs are re-pointed every render further down).
    const liveBHRef = useRef(() => 0);
    useEffect(() => { selectionRef.current = selection; }, [selection]);
    useEffect(() => { dataRef.current = data; }, [data]);
    useEffect(() => { pendingRef.current = pending; }, [pending]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/attendance/${year}/${month}`);
            setData(res.data); setLock(res.data.lock || null);
            setPending({}); setPendingHol({}); setPendingHolTreat({}); setPendingOverride({}); setPendingNotes({});
        }
        catch { toast.error('Failed to load attendance'); setData(null); }
        finally { setLoading(false); }
    }, [year, month]);
    useEffect(() => { load(); }, [load]);

    // Releasing the mouse ends the selection. One day → just cycle it (quick
    // repeated clicks still work). Several days → pop the status picker at the
    // cursor so you set them all at once instead of re-clicking each.
    useEffect(() => {
        const up = (e) => {
            if (!dragging.current) return;
            dragging.current = false;
            const sel = selectionRef.current;
            if (!sel || sel.days.size === 0) return;
            if (sel.days.size >= 2) {
                setMenu({ userId: sel.userId, days: [...sel.days].sort((a, b) => a - b), x: e.clientX, y: e.clientY });
                return;
            }
            // Single cell → cycle to the next status (fresh state via refs).
            const userId = sel.userId, day = [...sel.days][0];
            const s = (dataRef.current?.staff || []).find((x) => x.user_id === userId);
            if (s) {
                const CYCLE = s.is_cleaner ? CYCLE_CLEANER : CYCLE_STAFF;
                const p = pendingRef.current;
                const key = `${userId}-${day}`;
                const stored = s.marks[day] || null;
                const cur = (key in p) ? (p[key] === 'clear' ? null : p[key]) : stored;
                // Advance to the next status, but skip Annual Leave / UH when the
                // person has no balance (only selectable for those who do).
                const dim = dataRef.current.days_in_month;
                const idx = CYCLE.indexOf(cur);
                let next = null;
                for (let step = 1; step <= CYCLE.length; step++) {
                    const cand = CYCLE[(idx + step) % CYCLE.length];
                    if (cand === 'annual_leave' && annualRemainingFor(s, p, dim) < 1) continue;
                    if (cand === 'lieu_off' && liveBHRef.current(s) < 1) continue;
                    next = cand; break;
                }
                setPending((pp) => {
                    const np = { ...pp };
                    if (next === stored) delete np[key];
                    else np[key] = next === null ? 'clear' : next;
                    return np;
                });
            }
            setSelection(null);
        };
        window.addEventListener('mouseup', up);
        return () => window.removeEventListener('mouseup', up);
    }, []);

    // Dismiss the picker on an outside click or Escape.
    useEffect(() => {
        if (!menu) return;
        const onDown = (e) => { if (!e.target.closest || !e.target.closest('.att__menu')) { setMenu(null); setSelection(null); } };
        const onEsc = (e) => { if (e.key === 'Escape') { setMenu(null); setSelection(null); } };
        const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
        document.addEventListener('keydown', onEsc);
        return () => { clearTimeout(t); document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onEsc); };
    }, [menu]);

    // Dismiss the Holiday pay/bank popover on an outside click or Escape.
    useEffect(() => {
        if (!holMenu) return;
        const onDown = (e) => { if (!e.target.closest || !e.target.closest('.att__holMenu')) setHolMenu(null); };
        const onEsc = (e) => { if (e.key === 'Escape') setHolMenu(null); };
        const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
        document.addEventListener('keydown', onEsc);
        return () => { clearTimeout(t); document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onEsc); };
    }, [holMenu]);

    const dirtyCount = Object.keys(pending).length + Object.keys(pendingHol).length
        + Object.keys(pendingHolTreat).length + Object.keys(pendingOverride).length + Object.keys(pendingNotes).length;

    // Multi-admin friendliness: saves only send the cells YOU changed (server
    // merges per cell and re-checks balances against the live DB), so parallel
    // editors don't overwrite each other's rows. To also SEE colleagues' saved
    // work promptly, silently refresh whenever the tab regains focus — but never
    // while you hold unsaved changes (that would discard them).
    const dirtyRef = useRef(0);
    useEffect(() => { dirtyRef.current = dirtyCount; }, [dirtyCount]);
    useEffect(() => {
        const onFocus = () => { if (!dirtyRef.current) load(); };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [load]);

    // --- Edit lock driven by the FLOATING button ------------------------------
    // The bottom-right button is the whole flow: "✎ Start Editing" acquires the
    // month's lock and unlocks the grid; it then becomes the Save button, and a
    // successful save (or "Done editing" with nothing changed) frees the lock
    // for colleagues. It heartbeats while held and auto-expires after 2 min
    // idle, so an abandoned tab can't block anyone; the server refuses saves
    // from non-holders regardless.
    const holdingRef = useRef(false);
    const startEditing = useCallback(async () => {
        try {
            const res = await axios.post(`/api/attendance/${year}/${month}/lock`);
            holdingRef.current = true; setLock(res.data.lock); setEditMode(true);
        } catch (e) {
            if (e.response?.data?.lock) setLock(e.response.data.lock);
            toast.warn(e.response?.data?.error || 'Someone else is editing this month', { toastId: 'att-lock' });
        }
    }, [year, month]);
    const finishEditing = useCallback(() => {
        setEditMode(false); holdingRef.current = false;
        setLock((lk) => (lk?.mine ? null : lk));
        axios.delete(`/api/attendance/${year}/${month}/lock`).catch(() => { /* TTL frees it */ });
    }, [year, month]);
    // Leaving the month or the page releases whatever we hold (TTL covers crashes).
    useEffect(() => () => {
        if (holdingRef.current) { holdingRef.current = false; axios.delete(`/api/attendance/${year}/${month}/lock`).catch(() => {}); }
    }, [year, month]);
    useEffect(() => { setEditMode(false); }, [year, month]);
    // Heartbeat well inside the TTL while we hold it.
    useEffect(() => {
        if (!editMode) return;
        const t = setInterval(() => { axios.post(`/api/attendance/${year}/${month}/lock`).catch(() => {}); }, 45000);
        return () => clearInterval(t);
    }, [editMode, year, month]);
    const lockedByOther = !!(lock && !lock.mine);

    const step = (delta) => {
        if (dirtyCount && !window.confirm('Discard unsaved changes?')) return;
        let m = month + delta, y = year;
        if (m < 1) { m = 12; y -= 1; } if (m > 12) { m = 1; y += 1; }
        setMonth(m); setYear(y);
    };

    const effective = (s, day) => {
        const key = `${s.user_id}-${day}`;
        if (key in pending) return pending[key] === 'clear' ? null : pending[key];
        return s.marks[day] || null;
    };
    const isScheduled = (s, day) => s.scheduled.includes(day);
    const isHoliday = (day) => (day in pendingHol ? pendingHol[day] : (data?.holidays || []).includes(day));
    const isLocked = (s, day) => s.is_cleaner && isScheduled(s, day);

    // Mouse down starts a selection on this row (single click selects one day).
    const onCellDown = (e, s, day) => {
        if (isLocked(s, day)) return;
        if (!editMode) {
            toast.info(lockedByOther
                ? `${lock.name} is editing right now — view only until they finish (frees after 2 min idle).`
                : 'Click “✎ Start Editing” (bottom right) to make changes.', { toastId: 'att-lock' });
            return;
        }
        e.preventDefault();
        setMenu(null);
        dragging.current = true;
        setSelection({ userId: s.user_id, days: new Set([day]) });
    };
    // Dragging over more cells of the SAME person extends the selection.
    const onCellEnter = (s, day) => {
        if (!dragging.current || isLocked(s, day)) return;
        setSelection((sel) => {
            if (!sel || sel.userId !== s.user_id) return sel;
            const days = new Set(sel.days); days.add(day);
            return { userId: sel.userId, days };
        });
    };
    // Apply one status to every selected day at once.
    const applyStatusToDays = (userId, dayArr, status) => {
        const s = (data?.staff || []).find((x) => x.user_id === userId);
        if (!s) return;
        setPending((p) => {
            const np = { ...p };
            dayArr.forEach((day) => {
                const key = `${userId}-${day}`;
                const stored = s.marks[day] || null;
                if (status === 'clear') { if (stored === null) delete np[key]; else np[key] = 'clear'; }
                else if (status === stored) delete np[key];
                else np[key] = status;
            });
            return np;
        });
    };
    const applyMenu = (status) => {
        if (menu && (status === 'annual_leave' || status === 'lieu_off')) {
            const s = (data?.staff || []).find((x) => x.user_id === menu.userId);
            if (s) {
                const rem = status === 'lieu_off' ? liveBH(s) : annualRemainingFor(s, pending, days);
                const netNew = menu.days.filter((d) => {
                    const key = `${s.user_id}-${d}`;
                    const eff = key in pending ? (pending[key] === 'clear' ? null : pending[key]) : (s.marks[d] || null);
                    return eff !== status;
                }).length;
                if (netNew > rem) {
                    const what = status === 'lieu_off' ? 'unused holiday day' : 'annual-leave day';
                    toast.error(`Only ${Math.max(0, Math.floor(rem))} ${what}${Math.floor(rem) === 1 ? '' : 's'} left for ${s.name}.`);
                    return;
                }
            }
        }
        if (menu) applyStatusToDays(menu.userId, menu.days, status);
        setMenu(null); setSelection(null);
    };

    const setHolidayOn = (day, on) => {
        const stored = (data?.holidays || []).includes(day);
        setPendingHol((p) => {
            const np = { ...p };
            if (on === stored) delete np[day];
            else np[day] = on;
            return np;
        });
    };
    // Effective holiday default (pay/bank) for a day: pending → saved → 'pay'.
    const holidayDefault = (day) => (day in pendingHolTreat ? pendingHolTreat[day] : (data?.holidayTreatments?.[day] || 'pay'));
    const setHolidayDefault = (day, treatment) => setPendingHolTreat((p) => {
        const np = { ...p };
        if (treatment === (data?.holidayTreatments?.[day] || 'pay')) delete np[day]; else np[day] = treatment;
        return np;
    });
    // Saved per-person override for a worker on a holiday (from the loaded data).
    const savedOverride = (day, uid) => {
        const w = (data?.holidayWorkers?.[day] || []).find((x) => x.user_id === uid);
        return w ? (w.override || null) : null;
    };
    // Effective pay/bank for a worker on a holiday, honouring pending edits.
    const workerTreatment = (day, uid) => {
        const key = `${day}|${uid}`;
        const ov = key in pendingOverride ? (pendingOverride[key] === 'clear' ? null : pendingOverride[key]) : savedOverride(day, uid);
        return ov || holidayDefault(day);
    };
    const setWorkerTreatment = (day, uid, treatment) => {
        const key = `${day}|${uid}`;
        const savedOv = savedOverride(day, uid);
        // Store 'clear' when the choice matches the day default (no override row needed),
        // unless there is a saved override to remove.
        const wanted = treatment === holidayDefault(day) ? (savedOv ? 'clear' : null) : treatment;
        setPendingOverride((p) => {
            const np = { ...p };
            if (wanted === null || wanted === savedOv) delete np[key];
            else np[key] = wanted;
            return np;
        });
    };
    // Who worked a given day (present, not on leave) — computed live so the
    // popover works even for a holiday you just marked but haven't saved.
    const effMark = (s, day) => { const k = `${s.user_id}-${day}`; return k in pending ? (pending[k] === 'clear' ? null : pending[k]) : (s.marks[day] || null); };
    const isWorking = (s, day) => {
        const mk = effMark(s, day);
        return s.is_cleaner ? ((s.scheduled || []).includes(day) && !(mk && LEAVE_OFF_STATUSES.includes(mk))) : (mk === 'present');
    };
    const workersForDay = (day) => (data?.staff || []).filter((s) => isWorking(s, day));
    // Live per-person summary honouring pending edits so the right-hand columns
    // update as you edit (before saving). HP = paid holidays worked; banked ones
    // go to the BH balance instead. OT keeps the saved value (recomputed on save).
    const savedBankedThisMonth = (s) => (data?.holidays || []).reduce((n, day) => {
        const w = (data?.holidayWorkers?.[day] || []).find((x) => x.user_id === s.user_id);
        return n + (w && w.treatment === 'bank' ? 1 : 0);
    }, 0);
    const liveSummary = (s) => {
        let al = 0, ul = 0, hp = 0;
        for (let d = 1; d <= days; d++) {
            const mk = effMark(s, d);
            if (mk === 'annual_leave') al++;
            else if (mk === 'unpaid_leave') ul++;
            if (isHoliday(d) && isWorking(s, d) && workerTreatment(d, s.user_id) === 'pay') hp++;
        }
        return { al, ul, hp, ot: s.summary?.ot || 0 };
    };
    // UH balance shown live: saved balance, ± pending banked, ± pending taken.
    // This (not the saved-only figure) also gates the UH picker option, so a
    // holiday banked THIS session is spendable immediately, before saving.
    const liveBH = (s) => {
        let liveBanked = 0;
        for (let d = 1; d <= days; d++) if (isHoliday(d) && isWorking(s, d) && workerTreatment(d, s.user_id) === 'bank') liveBanked++;
        const bankDelta = liveBanked - savedBankedThisMonth(s);
        return lieuRemainingFor(s, pending, days) + bankDelta; // handles pending lieu_off taken (even with no saved credits)
    };
    liveBHRef.current = liveBH; // keep the mouseup handler's copy fresh
    const setNote = (uid, text) => setPendingNotes((p) => {
        const np = { ...p };
        const saved = (data?.staff || []).find((s) => s.user_id === uid)?.note || '';
        if (text === saved) delete np[uid]; else np[uid] = text;
        return np;
    });
    const noteFor = (s) => (s.user_id in pendingNotes ? pendingNotes[s.user_id] : (s.note || ''));
    // Click a Holiday-row cell → ensure it's a holiday, then open the pay/bank popover.
    const openHoliday = (day, e) => {
        if (!editMode) {
            toast.info(lockedByOther
                ? `${lock.name} is editing right now — view only until they finish (frees after 2 min idle).`
                : 'Click “✎ Start Editing” (bottom right) to make changes.', { toastId: 'att-lock' });
            return;
        }
        if (!isHoliday(day)) setHolidayOn(day, true);
        setHolMenu({ day, x: e.clientX, y: e.clientY });
    };

    const save = async () => {
        const changes = Object.entries(pending).map(([k, status]) => {
            const [user_id, day] = k.split('-');
            return { user_id: Number(user_id), day: Number(day), status };
        });
        // Every day whose holiday on/off OR default treatment changed.
        const holDays = new Set([...Object.keys(pendingHol), ...Object.keys(pendingHolTreat)].map(Number));
        const holiday_changes = [...holDays].map((day) => ({ day, on: isHoliday(day), treatment: holidayDefault(day) }));
        const treatment_overrides = Object.entries(pendingOverride).map(([k, treatment]) => {
            const [day, user_id] = k.split('|');
            return { day: Number(day), user_id: Number(user_id), treatment };
        });
        const notes = Object.entries(pendingNotes).map(([user_id, note]) => ({ user_id: Number(user_id), note }));
        if (!changes.length && !holiday_changes.length && !treatment_overrides.length && !notes.length) return;
        setSaving(true);
        try {
            setData((await axios.post(`/api/attendance/${year}/${month}/save`, { changes, holiday_changes, treatment_overrides, notes })).data);
            setPending({}); setPendingHol({}); setPendingHolTreat({}); setPendingOverride({}); setPendingNotes({});
            toast.success('Attendance saved');
            finishEditing(); // saving completes the session — the lock frees for colleagues
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to save'); }
        finally { setSaving(false); }
    };

    // Hide a staff member from the grid, or add them back.
    const exclude = async (userId, excluded, name) => {
        if (excluded && !window.confirm(`Hide ${name} from the attendance grid?`)) return;
        try { await axios.post('/api/attendance/exclude', { user_id: userId, excluded }); load(); }
        catch { toast.error('Failed to update'); }
    };

    const groups = useMemo(() => {
        const g = []; const byRole = {};
        (data?.staff || []).forEach((s) => {
            const role = s.role || 'other';
            if (!byRole[role]) { byRole[role] = []; g.push([role, byRole[role]]); }
            byRole[role].push(s);
        });
        return g;
    }, [data]);

    const days = data?.days_in_month || 30;
    const dayList = Array.from({ length: days }, (_, i) => i + 1);
    const dow = (d) => DOW[new Date(year, month - 1, d).getDay()];
    const isWeekend = (d) => { const wd = new Date(year, month - 1, d).getDay(); return wd === 0 || wd === 6; }; // UAE weekend: Sat + Sun
    // name + days + AL/UL/HP/OT/Left/BH + Notes.
    const totalCols = days + 8;

    const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);

    // For each employee, work out what unclassified (no explicit mark, not a
    // cleaner's scheduled day) days should show — only up to TODAY; future days
    // aren't judged yet:
    //   'wo'      → auto Weekly Off (a cleaner's first day off so far this week)
    //   'needs'   → red: a second+ day off, or any unmarked past day
    //   'neutral' → faint: a future day, not judged yet
    const derivedFor = (s) => {
        const map = {};
        const byWeek = {};
        for (let d = 1; d <= days; d++) {
            const dt = new Date(year, month - 1, d);
            const monday = new Date(dt); monday.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));
            const wk = monday.getTime();
            if (!byWeek[wk]) byWeek[wk] = { candidates: [], hasWO: false, worked: 0 };
            const st = effective(s, d);
            if (st === 'weekly_off') byWeek[wk].hasWO = true;
            // A public holiday the person is OFF on IS that week's rest day —
            // no additional auto Weekly Off gets allocated for the week.
            if (isHoliday(d) && !isWorking(s, d)) byWeek[wk].hasWO = true;
            // A day actually worked (a cleaner's scheduled day, or anyone marked Present).
            if ((s.is_cleaner && isScheduled(s, d)) || st === 'present') byWeek[wk].worked += 1;
            // A whole-day holiday shows H and is NOT a weekly-off candidate.
            const classified = !!st || (s.is_cleaner && isScheduled(s, d)) || isHoliday(d);
            if (!classified) byWeek[wk].candidates.push(d);
        }
        // A Weekly Off is ONE rest day inside a working week. More than 3 days
        // away in a row is an absence, not a rest day — every day of that run
        // stays red so the manager marks what actually happened (the owner
        // 2026-07-27). The run is measured across the month boundary via
        // prev_off_streak from the server.
        const didWork = (d) => (s.is_cleaner
            ? isScheduled(s, d) && !LEAVE_OFF_STATUSES.includes(effective(s, d))
            : effective(s, d) === 'present');
        const runLen = {};      // day → length of the not-worked run it belongs to
        let runStart = null;    // first day of the current run
        let carry = Number(s.prev_off_streak) || 0; // run days trailing from last month
        const closeRun = (endDay) => {
            if (runStart === null) return;
            const len = (endDay - runStart + 1) + (runStart === 1 ? carry : 0);
            for (let x = runStart; x <= endDay; x++) runLen[x] = len;
            runStart = null;
        };
        for (let d = 1; d <= days; d++) {
            if (didWork(d)) closeRun(d - 1);
            else if (runStart === null) runStart = d;
        }
        closeRun(days);

        Object.values(byWeek).forEach((w) => {
            let woUsed = w.hasWO; // an explicit Weekly Off already covers the week
            w.candidates.forEach((d) => {
                const dt = new Date(year, month - 1, d); dt.setHours(0, 0, 0, 0);
                if (dt > todayMid) { map[d] = 'neutral'; return; } // future — don't judge
                // Auto Weekly Off only if they actually worked that week — a week
                // with no working day (e.g. continuous leave) is all red, no WO —
                // and never inside a run of 4+ days away.
                const inLongRun = (runLen[d] || 0) > 3;
                if (s.is_cleaner && !woUsed && w.worked > 0 && !inLongRun) { map[d] = 'wo'; woUsed = true; }
                else map[d] = 'needs';
            });
        });
        return map;
    };

    return (
        <div className={`att ${standalone ? 'att--page' : 'att--tab'}`}>
            {standalone && <h1 className="att__title">Attendance &amp; Leaves</h1>}
            <div className="att__head">
                <div>
                    <p className="att__sub">Presence is set automatically from booking schedules — you only mark leaves, weekly offs &amp; holidays. Changes sync to payroll on save.</p>
                </div>
                <div className="att__monthNav">
                    <button className="att__navBtn" onClick={() => step(-1)}>‹</button>
                    <span className="att__month">{MONTHS[month - 1]} {year}</span>
                    <button className="att__navBtn" onClick={() => step(1)}>›</button>
                </div>
            </div>

            <details className="att__howto">
                <summary className="att__howtoBar">
                    <span className="att__noteIcon">ℹ️</span>
                    <span className="att__howtoTitle">How to use this page?</span>
                    <span className="att__howtoTeaser">presence · public holidays · leave types · editing — all explained</span>
                    <span className="att__howtoChevron">▾</span>
                </summary>
                <div className="att__howtoBody">
                    <section className="att__howtoSec">
                        <h4>Where presence comes from</h4>
                        <p><strong>Cleaners</strong> are filled in automatically: every day with a confirmed booking shows <span className="att__key att--p">P</span> <strong>Present</strong>. Those days can’t be changed here — if one is wrong, fix that day’s booking and the grid follows. <strong>Office staff, drivers and supervisors</strong> have no booking schedule, so their Present days must be marked by hand.</p>
                    </section>
                    <section className="att__howtoSec">
                        <h4>Editing the grid</h4>
                        <p>Press <strong>“✎ Start Editing”</strong> (bottom right) to unlock the month. <strong>Click</strong> a day to cycle through the statuses, or <strong>drag across several days</strong> to give them all one status at once. The same button becomes <strong>Save Changes</strong> — saving syncs everything straight to that month’s payroll. <span className="att__mark">Only one admin can edit a month at a time</span> — everyone else sees who’s editing and stays view-only until they save (the lock frees itself after 2 minutes idle).</p>
                    </section>
                    <section className="att__howtoSec">
                        <h4>Weekly Off &amp; red days</h4>
                        <p>Everyone gets <strong>one paid rest day per week</strong> (Mon–Sun): the first day off of a finished week fills in as <span className="att__key att--wo">WO</span> <strong>Weekly Off</strong> automatically. A <strong>second</strong> day off in the same week turns <span className="att__markRed">red</span> — that day still needs a leave type from you. Future days aren’t judged yet.</p>
                        <p><strong>Away 4 days or more in a row?</strong> That’s an absence, not a rest day, so <span className="att__markRed">every day of it stays red</span> with no automatic Weekly Off — mark what actually happened. A run that started in the previous month counts too.</p>
                    </section>
                    <section className="att__howtoSec">
                        <h4>Public holidays — mark them in the first row</h4>
                        <p>Public holidays are set on the <strong>🏖 Holiday row</strong> at the top of the grid. Click the holiday’s cell there to mark the whole day, then choose the <strong>default for everyone who works it</strong>: <em>Holiday pay</em> or <em>Bank as a day off</em>. After picking the default you can still change it <strong>per team member</strong> in the same popup. Anyone who doesn’t work the holiday shows <span className="att__key att--hol">H</span> — that already counts as the week’s rest day, so no extra Weekly Off is added.</p>
                        <ul className="att__guideList">
                            <li><span className="att__key att--hp">PH·P</span><span><strong>Present on Holiday · Paid</strong> — they worked the holiday and receive <strong>holiday pay</strong>. Counts in the <strong>HP</strong> column and appears as a 💡 suggestion on the Salaries tab so it isn’t forgotten at payroll time.</span></li>
                            <li><span className="att__key att--hb">PH·B</span><span><strong>Present on Holiday · Banked</strong> — they worked the holiday and instead <strong>bank a full day off</strong> to use later. Adds 1 to their <strong>UH</strong> balance; banked days never expire.</span></li>
                        </ul>
                    </section>
                    <section className="att__howtoSec">
                        <h4>Leave types</h4>
                        <ul className="att__guideList">
                            <li><span className="att__key att--al">AL</span><span><strong>Annual Leave</strong> — paid vacation. Counts against the balance (30 days per completed year of service, company-ID staff). Only selectable while days remain — the balance can’t go negative.</span></li>
                            <li><span className="att__key att--pl">PL</span><span><strong>Paid Leave</strong> — authorised paid absence (sickness, bereavement, emergency). <span className="att__mark">Only with management approval.</span> No salary effect.</span></li>
                            <li><span className="att__key att--ul">UL</span><span><strong>Unpaid Leave</strong> — any other absence. <span className="att__mark">Deducted automatically from that month’s salary</span> at the daily rate.</span></li>
                            <li><span className="att__key att--lieu">UH</span><span><strong>Unused Holiday Leave</strong> — a paid day off that spends one banked holiday. Only selectable for staff whose UH balance covers it.</span></li>
                        </ul>
                    </section>
                    <section className="att__howtoSec">
                        <h4>Totals &amp; notes on the right</h4>
                        <p>The summary columns update <strong>live while you edit</strong>, before saving: <strong>AL</strong> annual leave taken this month · <strong>UL</strong> unpaid leave days · <strong>HP</strong> holidays paid out · <strong>OT</strong> overtime (weeks worked with no day off) · <strong>Left</strong> annual leave remaining (30/yr, carried over) — counted to the end of the month you're viewing, with any leave already marked in <em>later</em> months shown under it as “+n booked” rather than deducted · <strong>UH</strong> banked holiday days still available. The <strong>Notes</strong> column takes a free note per person for the month.</p>
                    </section>
                </div>
            </details>

            <div className="att__hintRow">
                <span>Click a day to cycle · drag across days to pick one status for all · red still needs a mark.</span>
                {lockedByOther && (
                    <span className="att__lockHeld">🔒 {lock.name} is editing — view only (frees after 2 min idle)</span>
                )}
            </div>
            <div className="att__legend">
                <span className="att__key att--p">P Present</span>
                <span className="att__key att--wo">WO Weekly Off</span>
                <span className="att__key att--al">AL Annual Leave</span>
                <span className="att__key att--pl">PL Paid Leave</span>
                <span className="att__key att--ul">UL Unpaid Leave</span>
                <span className="att__key att--hol">H Holiday (off)</span>
                <span className="att__key att--hp">PH·P Present on Holiday · Paid</span>
                <span className="att__key att--hb">PH·B Present on Holiday · Banked</span>
                <span className="att__key att--lieu">UH Unused Holiday Leave</span>
            </div>

            {loading ? <div className="att__empty">Loading…</div> : !data?.staff?.length ? (
                <div className="att__empty">No staff found.</div>
            ) : (
                <div className="att__scroll">
                    <table className="att__table">
                        <thead>
                            <tr>
                                <th className="att__nameCol">Name</th>
                                {dayList.map((d) => (
                                    <th key={d} className={`att__dayHead${isHoliday(d) ? ' att--holcol' : ''}${isWeekend(d) ? ' att--weekend' : ''}`}>
                                        <span className="att__dow">{dow(d)}</span>
                                        <span className="att__dnum">{d}</span>
                                    </th>
                                ))}
                                <th className="att__sumHead att--sumal" title="Annual leave taken this month">AL</th>
                                <th className="att__sumHead att--sumul" title="Unpaid Leave">UL</th>
                                <th className="att__sumHead att--sumhp" title="Holidays PAID this month (worked holidays paid out)">HP</th>
                                <th className="att__sumHead att--sumot" title="Overtime (weeks with no day off)">OT</th>
                                <th className="att__sumHead att--sumleft" title="Annual leave remaining (30/yr, carried over) to the end of this month. Leave marked in later months shows as '+n booked'.">Left</th>
                                <th className="att__sumHead att--sumlieu" title="Unused holiday days available (worked holidays banked as time off, minus taken)">UH</th>
                                <th className="att__sumHead att--sumnote">Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Whole-day company Holiday toggles. */}
                            <tr className="att__holRow">
                                <td className="att__nameCol att__holLabel">🏖 Holiday</td>
                                {dayList.map((d) => {
                                    const on = isHoliday(d);
                                    const dirty = (d in pendingHol) || (d in pendingHolTreat) || Object.keys(pendingOverride).some((k) => k.startsWith(`${d}|`));
                                    const treat = on ? holidayDefault(d) : null;
                                    return (
                                        <td key={d}
                                            className={`att__cell att__holCell${on ? ' att--hol att--holcol' : ''}${dirty ? ' is-dirty' : ''}`}
                                            title={on ? `Public holiday — default: ${treat === 'bank' ? 'Bank as day off' : 'Holiday pay'}. Click to set pay/bank.` : 'Mark this whole day a holiday'}
                                            onClick={(e) => openHoliday(d, e)}>
                                            {on ? (treat === 'bank' ? 'H·B' : 'H') : ''}
                                        </td>
                                    );
                                })}
                                <td className="att__sumCell" colSpan={7}></td>
                            </tr>
                            {groups.map(([role, rows]) => (
                                <React.Fragment key={role}>
                                    <tr className="att__roleRow">
                                        <td className="att__roleLabel">{role}</td>
                                        <td className="att__roleFill" colSpan={totalCols - 1}></td>
                                    </tr>
                                    {rows.map((s) => {
                                        const sm = liveSummary(s);
                                        const bh = liveBH(s);
                                        const derived = derivedFor(s);
                                        return (
                                            <tr key={s.user_id} className="att__staffRow">
                                                <td className="att__nameCol att__name">
                                                    <span className="att__nameText">{s.name}</span>
                                                    <button className="att__rm" title="Hide from attendance" onClick={() => exclude(s.user_id, true, s.name)}>✕</button>
                                                </td>
                                                {dayList.map((d) => {
                                                    const st = effective(s, d);
                                                    const sched = isScheduled(s, d);
                                                    const hol = isHoliday(d);
                                                    const locked = isLocked(s, d);
                                                    const key = `${s.user_id}-${d}`;
                                                    const dirty = key in pending;
                                                    const der = derived[d];
                                                    const selected = (selection && selection.userId === s.user_id && selection.days.has(d))
                                                        || (menu && menu.userId === s.user_id && menu.days.includes(d));
                                                    let code = '', cls = '', title = '';
                                                    if (hol && isWorking(s, d)) {
                                                        // Present on a company holiday → PH·P (paid) / PH·B (banked).
                                                        const t = workerTreatment(d, s.user_id);
                                                        code = t === 'bank' ? 'PH·B' : 'PH·P';
                                                        cls = t === 'bank' ? 'att--hb' : 'att--hp';
                                                        title = t === 'bank' ? 'Present on Holiday — banked as a day off (UH)' : 'Present on Holiday — paid';
                                                    } else if (hol && st === 'weekly_off') {
                                                        // Never show WO on a public holiday — the holiday IS the day off.
                                                        code = 'H'; cls = 'att--hol';
                                                        title = 'Public holiday — day off (counts as the week’s rest day)';
                                                    } else if (st && META[st]) {
                                                        // An explicit mark wins — e.g. annual leave stays annual leave.
                                                        code = META[st].code; cls = META[st].cls;
                                                        title = META[st].label;
                                                    } else if (s.is_cleaner && sched) {
                                                        code = 'P'; cls = 'att--p';
                                                        title = 'Present (from schedule — locked)';
                                                    } else if (hol) {
                                                        // Off on a company holiday → H (not their weekly off, not red).
                                                        code = 'H'; cls = 'att--hol';
                                                        title = 'Public holiday — day off';
                                                    } else if (der === 'wo') {
                                                        code = 'WO'; cls = 'att--wo att--auto';
                                                        title = 'Weekly Off (auto — first day off this week)';
                                                    } else if (der === 'needs') {
                                                        code = '·'; cls = 'att--needs';
                                                        title = 'Needs a mark — extra day off; set Weekly Off or a leave';
                                                    } else {
                                                        code = ''; cls = 'att--neutral';
                                                        title = 'Week not finished yet';
                                                    }
                                                    return (
                                                        <td key={d}
                                                            className={`att__cell ${cls}${hol ? ' att--holcol' : ''}${dirty ? ' is-dirty' : ''}${locked ? ' att--locked' : ''}${selected ? ' is-selected' : ''}`}
                                                            title={title}
                                                            onMouseDown={(e) => onCellDown(e, s, d)}
                                                            onMouseEnter={() => onCellEnter(s, d)}>
                                                            {code}
                                                        </td>
                                                    );
                                                })}
                                                <td className="att__sumCell att--sumal">{sm.al || ''}</td>
                                                <td className="att__sumCell att--sumul">{sm.ul || ''}</td>
                                                <td className="att__sumCell att--sumhp">{sm.hp || ''}</td>
                                                <td className="att__sumCell att--sumot">{sm.ot || ''}</td>
                                                {(() => {
                                                    const a = annualFor(s, pending, days);
                                                    if (!a) return <td className="att__sumCell att--sumleft" title="Company-ID staff only">—</td>;
                                                    const title = `${s.annual_left.accrued} accrued − ${s.annual_left.accrued - a.left} taken to end of this month`
                                                        + (a.ahead ? `\n${a.ahead} more already booked in later months → ${a.bookable} still bookable` : '');
                                                    return (
                                                        <td className="att__sumCell att--sumleft" title={title}>
                                                            {a.left}
                                                            {a.ahead > 0 && <span className="att__leftAhead">+{a.ahead}&nbsp;booked</span>}
                                                        </td>
                                                    );
                                                })()}
                                                <td className="att__sumCell att--sumlieu" title="Unused holiday days available (earned − taken)">{(s.lieu || bh !== 0) ? bh : '—'}</td>
                                                <td className="att__sumCell att__noteCell">
                                                    <input className="att__noteInput" type="text" value={noteFor(s)} placeholder="—" disabled={!editMode}
                                                        onChange={(e) => setNote(s.user_id, e.target.value)} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {data?.hidden?.length > 0 && (
                <div className="att__hidden">
                    <div className="att__hiddenTitle">Hidden from attendance · {data.hidden.length}</div>
                    <div className="att__hiddenList">
                        {data.hidden.map((h) => (
                            <div key={h.user_id} className="att__hiddenItem">
                                <span className="att__hiddenName">{h.name}</span>
                                <span className="att__hiddenRole">{h.role}</span>
                                <button className="att__addBack" onClick={() => exclude(h.user_id, false, h.name)}>+ Add back</button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {menu && (() => {
                const ms = (data?.staff || []).find((x) => x.user_id === menu.userId);
                const opts = MENU_OPTIONS.filter((o) => {
                    if (o === 'present') return ms && !ms.is_cleaner;
                    // Leave types you can only pick if the person actually has the balance.
                    if (o === 'lieu_off') return ms && liveBH(ms) > 0;   // unused holiday — incl. banked this session (unsaved)
                    if (o === 'annual_leave') return ms && annualRemainingFor(ms, pending, days) > 0; // annual leave left
                    return true;
                });
                return (
                    <div className="att__menu" style={{ left: Math.min(menu.x, window.innerWidth - 200), top: Math.min(menu.y, window.innerHeight - 260) }}>
                        <div className="att__menuHead">{menu.days.length} day{menu.days.length === 1 ? '' : 's'} · set to</div>
                        {opts.map((o) => (
                            <button key={o} className={`att__menuItem ${META[o].cls}`} onClick={() => applyMenu(o)}>
                                <span className="att__menuCode">{META[o].code}</span>{META[o].label}
                            </button>
                        ))}
                        <button className="att__menuItem att__menuClear" onClick={() => applyMenu('clear')}>Clear mark</button>
                    </div>
                );
            })()}

            {holMenu && (() => {
                const d = holMenu.day;
                const def = holidayDefault(d);
                const workers = workersForDay(d);
                return (
                    <div className="att__holMenu" style={{ left: Math.min(holMenu.x, window.innerWidth - 320), top: Math.min(holMenu.y, window.innerHeight - 360) }}>
                        <div className="att__holMenuHead">
                            <span>🏖 Holiday · {MONTHS[month - 1]} {d}</span>
                            <button className="att__holMenuRemove" onClick={() => { setHolidayOn(d, false); setHolMenu(null); }}>Remove</button>
                        </div>
                        <div className="att__holMenuLabel">Everyone who worked it:</div>
                        <div className="att__holToggle">
                            <button className={`att__holToggleBtn${def === 'pay' ? ' is-on' : ''}`} onClick={() => setHolidayDefault(d, 'pay')}>Holiday pay</button>
                            <button className={`att__holToggleBtn att--bank${def === 'bank' ? ' is-on' : ''}`} onClick={() => setHolidayDefault(d, 'bank')}>Bank as day off</button>
                        </div>
                        <div className="att__holMenuLabel att__holMenuLabel--sub">Worked this day ({workers.length}) — override individually:</div>
                        <div className="att__holWorkers">
                            {workers.length === 0 && <div className="att__holWorkersEmpty">No one is scheduled/present yet.</div>}
                            {workers.map((s) => {
                                const t = workerTreatment(d, s.user_id);
                                return (
                                    <div key={s.user_id} className="att__holWorker">
                                        <span className="att__holWorkerName">{s.name}</span>
                                        <div className="att__holWorkerToggle">
                                            <button className={`att__holWorkerBtn${t === 'pay' ? ' is-on' : ''}`} onClick={() => setWorkerTreatment(d, s.user_id, 'pay')}>Pay</button>
                                            <button className={`att__holWorkerBtn att--bank${t === 'bank' ? ' is-on' : ''}`} onClick={() => setWorkerTreatment(d, s.user_id, 'bank')}>Bank</button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="att__holMenuFoot">Banked days become Unused Holiday Leave (UH) the staff can take later as paid time off. Save to apply.</div>
                    </div>
                );
            })()}

            {/* The floating button IS the edit flow: Start Editing → Save → done.
                While a colleague holds the lock it shows who (clicking retries —
                which also takes over automatically once their lock goes stale). */}
            {editMode ? (
                <button className="att__save" onClick={dirtyCount ? save : finishEditing} disabled={saving}>
                    {saving ? 'Saving…' : dirtyCount ? `Save ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}` : 'Save Changes'}
                </button>
            ) : (
                <button className={`att__save ${lockedByOther ? 'att__save--locked' : 'att__save--start'}`} onClick={startEditing} disabled={loading}>
                    {lockedByOther ? `🔒 ${lock.name} is editing` : '✎ Start Editing'}
                </button>
            )}
        </div>
    );
}
